import React, { useState, useEffect } from 'react'
import { Phone, SquarePen, TrashIcon } from 'lucide-react';
import EmployeeGroupForm from 'features/company/components/employee-groups/EmployeeGroupForm';
import EmployeesList from 'features/company/components/employees/EmployeesList';
import { Tabs, Tab } from 'shared/ui/Tabs';
import { employeeGroupService } from 'features/company/api';
import { useStateContext } from 'shared/contexts/ContextProvider'
import { useLayout } from 'shared/contexts/LayoutContext'

const EmployeeGroupItem = ({ item, refreshList }) => {
  const [activeTab, setActiveTab] = useState('employees')
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(false)
  const { openModal, closeModal } = useLayout();
  const { token, showToast, currentColor } = useStateContext()

  useEffect(() => {
    // Fetch sections when component mounts
    if (item && item.id) {
      fetchSections()
    }
  }, [item])

  const fetchSections = async () => {
    try {
      setLoading(true)
      const response = await employeeGroupService.getSections(item.id, token)
      console.log('response', response.response.rows)
      setSections(response.response.rows || [])
    } catch (error) {
      showToast({
        title: 'Error!',
        content: error.message || 'Failed to load sections.',
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCheckboxChange = async (sectionId) => {
    try {
      // Find the section in our local state
      const sectionIndex = sections.findIndex(section => section.id === sectionId)
      if (sectionIndex === -1) return

      // Create a copy of the sections array
      const updatedSections = [...sections]
      // Toggle the included property
      updatedSections[sectionIndex].included = !updatedSections[sectionIndex].included
      // Update local state first for immediate visual feedback
      setSections(updatedSections)

      // Then send the update to the server
      await employeeGroupService.updateSection(
        item.id,
        sectionId,
        updatedSections[sectionIndex].included,
        token
      )

      // Optional: Refresh list if needed
      if (refreshList) refreshList()
    } catch (error) {
      // If there's an error, revert the local change
      showToast({
        title: 'Error!',
        content: error.message || 'Failed to update section access.',
        type: 'error'
      })
      fetchSections() // Re-fetch to restore correct state
    }
  }

  const handleDeleteEmployeeGroup = async () => {
    const confirmDelete = window.confirm(`Вы уверены, что хотите удалить группу сотрудников ${item.name}?`) 
    if (confirmDelete) {
      const response = await employeeGroupService.delete(item.id, token)
      if(response.status === 0) {
        showToast({
          title: 'Успешно!',
          content: `Группа сотрудников ${item.name} удалена`,
          type: 'success',
        })
        closeModal();
        refreshList ? refreshList() : null
      }
      else {
        showToast({
          title: 'Ошибка!',
          content: response.message || 'Что-то пошло не так!',
          type: 'error',
        })
      }
    }
  }

  const handleEditEmployeeGroup = () => {
    openModal({
      component: <EmployeeGroupForm employeeGroup={item} refreshList={refreshList} />,
      title: 'Изменить группу сотрудников',
    })
  }

  return (
    <div className="p-4">
      <div className='flex justify-between items-start mb-6'>
        <div>
          <h1 className='text-xl font-semibold mb-1'>{item.name}</h1> 
          <p className='text-gray-500 text-xs'>Создан {item.created}</p> 
        </div>
        <div className='text-right space-x-2'>
          <button className=' text-white px-4 py-2 rounded-md hover:' style={{ backgroundColor: currentColor }} onClick={() => handleEditEmployeeGroup()}>
            <SquarePen size={16} className='mr-1' /> 
          </button>
          <button className=' text-white px-4 py-2 rounded-md hover: bg-red-500' onClick={() => handleDeleteEmployeeGroup()}>
            <TrashIcon size={16} className='mr-1' />
          </button>
        </div>
      </div>
      <Tabs initialTab="employees">
        <Tab key="employees" label="Сотрудники">
          <EmployeesList group={item} />
        </Tab>
        <Tab key="sections" label="Доступные разделы">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loader"></div>
            </div>
          ) : (
            <div className="mb-4">
              {sections && sections.length > 0 ? (
                sections.map((section) => (
                  <div
                    key={section.id}
                    onClick={() => handleCheckboxChange(section.id)}
                    className={`${
                      section.included ? 'bg-gray-200' : 'bg-white'
                    } p-4 mb-2 shadow-md rounded-lg hover:bg-gray-300 transition-colors duration-200`}
                  >
                    <label className="flex items-center text-gray-800 text-md font-semibold mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={section.included}
                        onChange={(e) => {
                          e.stopPropagation() // Prevent double triggering
                          handleCheckboxChange(section.id)
                        }}
                        className="form-checkbox h-5 w-5 text-blue-600 mr-2"
                      />
                      {section.name}
                    </label>
                    <div className="ml-6">
                      {section.sub_sections && section.sub_sections.rows.map((subSection) => (
                        <p
                          key={subSection.url}
                          className="text-gray-600 text-sm my-1 transition-colors duration-200"
                        >
                          {subSection.name}
                        </p>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No sections available</p>
              )}
            </div>
          )}
        </Tab>
      </Tabs>
    </div>
  )
}

export default EmployeeGroupItem